import useLatest from "@-/com.npmjs.use-latest";
import { learnCardPresentationKey } from "@-/dev.improve.haniverse.lib.learn.card-action";
import {
  flashcardQueueSuccessor,
  type LearnDeckEvent,
  type LearnDeckState,
} from "@-/dev.improve.haniverse.lib.learn.deck";
import type { LearnCardControls } from "@-/dev.improve.haniverse.routes.nav.app.learn.deck.context";
import { useCallback, useEffect, useState, type Dispatch, type RefObject } from "react";

/** The next flashcard's presentation, which a released card uncovers before React promotes it. */
function successorPresentationKey(state: LearnDeckState): string | null {
  const successor = flashcardQueueSuccessor(state);
  return successor?.kind === "card" ? learnCardPresentationKey(successor.slot.presentation) : null;
}

/**
 * A reveal is owed to a presentation even if React never renders its dragging phase.
 *
 * A drag can also start on the successor a released card uncovered, before React has promoted it.
 * Its reveal is owed too: it is kept (with no motion generation of its own yet) until that
 * presentation becomes current, and dropped if a different card does.
 */
export function useLearnCardDragStart({
  cardControls,
  dispatch,
  isFocused,
  motionEpochRef,
  state,
}: {
  cardControls: LearnCardControls | null;
  dispatch: Dispatch<LearnDeckEvent>;
  isFocused: boolean;
  motionEpochRef: RefObject<number>;
  state: LearnDeckState;
}) {
  const stateRef = useLatest(state);
  const [request, setRequest] = useState<{
    /** `null` for a card that was not current yet when its drag started. */
    motionEpoch: number | null;
    presentationKey: string;
  } | null>(null);
  const presentationKey = learnCardPresentationKey(state.current.presentation);
  const successorKey = successorPresentationKey(state);
  const phase = state.transition.phase;
  useEffect(() => {
    if (request === null) {
      return;
    }
    if (
      request.motionEpoch === null &&
      request.presentationKey !== presentationKey &&
      request.presentationKey === successorKey
    ) {
      // Still waiting for its promotion.
      return;
    }
    if (
      request.presentationKey !== presentationKey ||
      (request.motionEpoch !== null && request.motionEpoch !== motionEpochRef.current) ||
      state.face !== "front" ||
      state.view !== "study" ||
      (phase !== "idle" && phase !== "dragging")
    ) {
      setRequest(null);
      return;
    }
    // Suspense or a blurred tab may temporarily withdraw the controls (and a slide or a quiz has no
    // reveal). Retain the request until its presentation can service it, rather than leaving the
    // UI-only answer inert.
    if (
      !isFocused ||
      cardControls === null ||
      cardControls.kind === "slide" ||
      cardControls.kind === "quiz"
    ) {
      return;
    }
    setRequest(null);
    // Use the existing reveal path for audio, keyboard focus and lesson entitlement gating.
    // Horizontal reveals do not animate, so they are safe after a short drag has ended too.
    cardControls.reveal();
  }, [
    cardControls,
    isFocused,
    motionEpochRef,
    phase,
    presentationKey,
    request,
    state.face,
    state.view,
    successorKey,
  ]);

  return useCallback(
    (presentationKey: string, motionEpoch: number) => {
      const current = stateRef.current;
      if (learnCardPresentationKey(current.current.presentation) === presentationKey) {
        if (motionEpochRef.current !== motionEpoch) {
          return;
        }
        dispatch({ type: "drag-start" });
        setRequest({ motionEpoch, presentationKey });
        return;
      }
      if (successorPresentationKey(current) === presentationKey) {
        setRequest({ motionEpoch: null, presentationKey });
      }
    },
    [dispatch, motionEpochRef, stateRef],
  );
}
